/**
 * Turn axios / network failures into clear, user-facing copy.
 * Prefer backend `detail` when present; otherwise map status / network cases.
 */

const STEP_SAVE_HINTS = {
    overview: 'Check trip title, country, and overview fields, then try again.',
    packages: 'Check package details (duration, cities, status), then try again.',
    pricing: 'Check base price, currency, and selected payment templates, then try again.',
    services: 'Check each trip logistics item has a name, category, and valid cost, then try again.',
    departures: 'Check departure and return dates match the package duration, then try again.',
    allocations: 'Check merchant, departure date, and trip logistics, then try again.',
    itineraries: 'Check day titles, descriptions, and line items, then try again.',
    addons: 'Check add-on details and linked trip logistics, then try again.',
    faqs: 'Check FAQ questions and answers, then try again.',
    review: 'Review the trip summary and try saving again.',
};

const extractDetail = (data) => {
    if (!data) return null;
    if (typeof data === 'string') return data;
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail)) {
        return data.detail
            .map((item) => {
                if (typeof item === 'string') return item;
                if (item?.msg) {
                    const loc = Array.isArray(item.loc) ? item.loc.filter((p) => p !== 'body').join(' → ') : '';
                    return loc ? `${loc}: ${item.msg}` : item.msg;
                }
                return null;
            })
            .filter(Boolean)
            .join(' ');
    }
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;
    return null;
};

export const getApiErrorMessage = (error, { action = 'save', stepKey } = {}) => {
    const stepHint = STEP_SAVE_HINTS[stepKey] || 'Please review this step and try again.';
    const detail = extractDetail(error?.response?.data);
    const status = error?.response?.status;

    if (!error?.response) {
        if (error?.code === 'ECONNABORTED') {
            return `The ${action} request timed out. Check your connection and try again.`;
        }
        return `Unable to ${action} — no response from the server. Check your internet connection and try again.`;
    }

    if (status === 401 || status === 403) {
        return `Your session expired or you do not have permission to ${action}. Please sign in again.`;
    }

    if (status === 404) {
        return `The trip could not be found. It may have been deleted. Refresh the page or return to Trips.`;
    }

    if (status === 409) {
        return detail || `This trip conflicts with existing data. ${stepHint}`;
    }

    if (status === 422 || status === 400) {
        return detail
            ? `Unable to ${action}: ${detail}`
            : `Some fields on this step are invalid. ${stepHint}`;
    }

    if (status >= 500) {
        return `The server failed while trying to ${action}. Please wait a moment and try again.`;
    }

    if (detail) {
        return `Unable to ${action}: ${detail}`;
    }

    return `Unable to ${action} this step. ${stepHint}`;
};

export default getApiErrorMessage;
